import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// Mostra, in modo dinamico, il sottoconto CO.GE. di Stato Patrimoniale che verrà
// movimentato per questo nominativo (cliente o fornitore). Il sottoconto è risolto
// lato backend da categoria + area Italia/Estero (dedotta dalla sede legale).
// Se manca l'associazione, mostra un alert con il rimando a "Associazioni conti".

interface ContoRef { id: number; codice: string; descrizione: string; attivo?: boolean }
interface Risoluzione { categoria: string | null; conto: ContoRef | null }
interface SottocontiResp {
  area: 'ITALIA' | 'ESTERO';
  areaPaese: string | null;
  areaFonte: 'SEDE_LEGALE' | 'PRINCIPALE' | 'NESSUNO';
  cliente: Risoluzione | null;
  fornitore: Risoluzione | null;
}

interface Props {
  nominativoId: number;
  tipo: 'CLIENTE' | 'FORNITORE';
  categoria: string; // forza il refresh quando cambia la categoria
}

const AREA_LABEL: Record<'ITALIA' | 'ESTERO', string> = { ITALIA: 'Italia', ESTERO: 'Estero' };

export default function SottocontoCogeInfo({ nominativoId, tipo, categoria }: Props) {
  const [data, setData] = useState<SottocontiResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // @api GET /api/nominativi/:id/sottoconti-coge -> { success: true, data: { area, areaPaese, areaFonte, cliente: { categoria, conto }|null, fornitore: { categoria, conto }|null } }
        const res = await apiFetch<{ success: boolean; data: SottocontiResp }>(
          `/api/nominativi/${nominativoId}/sottoconti-coge`,
        );
        if (alive) setData(res.data);
      } catch (e: any) {
        if (alive) setError(e.message || 'Errore nel calcolo del sottoconto');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [nominativoId, tipo, categoria]);

  const tipoLabel = tipo === 'CLIENTE' ? 'cliente' : 'fornitore';
  const ris = tipo === 'CLIENTE' ? data?.cliente : data?.fornitore;
  const conto = ris?.conto ?? null;
  const area = data?.area;
  const areaLabel = area ? AREA_LABEL[area] : '—';

  return (
    <div className="bop-card bop-card__body">
      <h3 className="bop-anagrafica__section-title">Sottoconto contabile</h3>

      {loading ? (
        <div className="bop-empty-state">Calcolo del sottoconto…</div>
      ) : error ? (
        <div className="bop-inline-warning">{error}</div>
      ) : (
        <>
          <p className="bop-text-muted">
            Conto di Stato Patrimoniale che verrà movimentato dai movimenti contabili verso questo {tipoLabel},
            in base alla categoria e all'area geografica.
          </p>

          <div className="bop-flex">
            <span className="bop-text-muted">Area:</span>
            <span className="bop-badge bop-badge--info">{areaLabel}</span>
            {data?.areaFonte === 'NESSUNO' && (
              <span className="bop-text-muted">(nessuna sede legale: assunta Italia)</span>
            )}
          </div>

          <div className="bop-flex">
            <span className="bop-text-muted">Sottoconto:</span>
            {conto ? (
              <strong>{conto.codice} — {conto.descrizione}{conto.attivo === false ? ' (disattivo)' : ''}</strong>
            ) : (
              <span>Non associato</span>
            )}
          </div>

          {!conto && (
            <div className="bop-inline-warning">
              ⚠️ Nessun sottoconto CO.GE. associato per la categoria <strong>{ris?.categoria ?? categoria}</strong> e
              l'area <strong>{areaLabel}</strong>. Configura l'associazione in <strong>Contabilità Generale → Associazioni conti</strong>.
            </div>
          )}

          {conto && conto.attivo === false && (
            <div className="bop-inline-warning">
              ⚠️ Il sottoconto associato è <strong>disattivato</strong>. Aggiorna l'associazione in <strong>Associazioni conti</strong>.
            </div>
          )}
        </>
      )}
    </div>
  );
}
